import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDialogActions } from "~/store/dialogStore";
import { UnsavedChangesDialog } from "~/components/UnsavedChangesDialog";
import { AlertTriangle } from "lucide-react";

interface UseBackButtonGuardProps {
  hasUnsavedChanges: boolean;
  onConfirmNavigation?: () => void;
}

/**
 * Deve ser usado em apenas UM componente (layout) para evitar
 * múltiplas entradas dummy no histórico do navegador.
 */
export function useBackButtonGuard({
  hasUnsavedChanges,
  onConfirmNavigation,
}: UseBackButtonGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { openDialog, closeDialog, setPendingConfirmAction } = useDialogActions();
  const previousPathnameRef = useRef(location.pathname);
  const hasDummyEntryRef = useRef(false);

  useEffect(() => {
    return () => {
      previousPathnameRef.current = location.pathname;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      hasDummyEntryRef.current = false;
      return;
    }

    if (!hasDummyEntryRef.current) {
      window.history.pushState({ unsavedGuard: true }, "", location.pathname);
      hasDummyEntryRef.current = true;
    }

    const handlePopState = (e: PopStateEvent) => {
      // Recoloca a entrada dummy para manter o usuário na página
      window.history.pushState({ unsavedGuard: true }, "", location.pathname);

      const targetPath = previousPathnameRef.current;

      const confirmAction = () => {
        onConfirmNavigation?.();
        hasDummyEntryRef.current = false;
        navigate(targetPath, { replace: true });
      };

      setPendingConfirmAction(confirmAction);

      openDialog({
        title: "Alterações não salvas",
        icon: <AlertTriangle />,
        content: <UnsavedChangesDialog onCancel={closeDialog} />,
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges, location.pathname, navigate, openDialog, closeDialog, setPendingConfirmAction, onConfirmNavigation]);
}
